import { CsvColumn, SpreadSheetService } from "../../s25-spreadsheet/spreadsheet.service";
import { SheetEntry } from "../../s25-spreadsheet/types/sheet.js.types.expanded";
import { S25Util } from "../../../util/s25-util";
import { Budget } from "./Budget";
import { OrganizationExport } from "../export/organization.export.types";

export type OrganizationExportFlattenOptions = {
    budget?: Partial<OrganizationExport.BudgetOptions>;
};

/** Column role for budget spreadsheet round-trip (export headers = import headers). */
export type BudgetSpreadsheetColumnRole = "identity" | "writable" | "readonly";

export type BudgetSpreadsheetColumnKey =
    | "organizationId"
    | "organizationName"
    | "fiscalYearName"
    | "budgetName"
    | "orgBudgetDescription"
    | "totalAllocated"
    | "orgBudgetTotalSpent"
    | "orgBudgetTotalCommitted"
    | "orgBudgetUtilization"
    | "planName"
    | "planType"
    | "planDescription"
    | "planStatus"
    | "planAllocatedAmount"
    | "planStartDate"
    | "planEndDate"
    | "lineItemName"
    | "lineCategoryName"
    | "lineItemDescription"
    | "lineItemComments"
    | "linePlannedAmount"
    | "lineItemActualAmount"
    | "lineItemCommittedAmount";

export type BudgetSpreadsheetColumnDef = {
    key: BudgetSpreadsheetColumnKey;
    header: string;
    role: BudgetSpreadsheetColumnRole;
};

/**
 * Single source of truth for budget export labels and import header mapping.
 * Identity + writable columns are required for a happy-path re-import; readonly
 * columns may appear on export and are ignored on import.
 */
export const BUDGET_SPREADSHEET_COLUMNS: readonly BudgetSpreadsheetColumnDef[] = [
    { key: "organizationId", header: "Organization Id", role: "identity" },
    { key: "organizationName", header: "Organization", role: "identity" },
    { key: "fiscalYearName", header: "Fiscal Year", role: "identity" },
    { key: "budgetName", header: "Org Budget Name", role: "writable" },
    { key: "orgBudgetDescription", header: "Org Budget Description", role: "writable" },
    { key: "totalAllocated", header: "Org Budget Total Allocated", role: "writable" },
    { key: "orgBudgetTotalSpent", header: "Org Budget Total Spent", role: "readonly" },
    { key: "orgBudgetTotalCommitted", header: "Org Budget Total Committed", role: "readonly" },
    { key: "orgBudgetUtilization", header: "Utilization", role: "readonly" },
    { key: "planName", header: "Budget Plan Name", role: "writable" },
    { key: "planType", header: "Budget Plan Type", role: "writable" },
    { key: "planDescription", header: "Budget Plan Description", role: "writable" },
    { key: "planStatus", header: "Plan Status", role: "readonly" },
    { key: "planAllocatedAmount", header: "Plan Allocated Amount", role: "writable" },
    { key: "planStartDate", header: "Plan Start Date", role: "writable" },
    { key: "planEndDate", header: "Plan End Date", role: "writable" },
    { key: "lineItemName", header: "Line Item Name", role: "writable" },
    { key: "lineCategoryName", header: "Line Item Category", role: "writable" },
    { key: "lineItemDescription", header: "Line Item Description", role: "writable" },
    { key: "lineItemComments", header: "Line Item Comments", role: "writable" },
    { key: "linePlannedAmount", header: "Line Item Planned Amount", role: "writable" },
    { key: "lineItemActualAmount", header: "Line Item Actual Amount", role: "readonly" },
    { key: "lineItemCommittedAmount", header: "Line Item Committed Amount", role: "readonly" },
];

export type BudgetExportFlatRow = {
    [K in BudgetSpreadsheetColumnKey]: string | number;
};

const IDENTITY_KEYS: BudgetSpreadsheetColumnKey[] = BUDGET_SPREADSHEET_COLUMNS.filter(
    (column) => column.role === "identity",
).map((column) => column.key);

const ORG_BUDGET_KEYS: BudgetSpreadsheetColumnKey[] = [
    "budgetName",
    "orgBudgetDescription",
    "totalAllocated",
    "orgBudgetTotalSpent",
    "orgBudgetTotalCommitted",
    "orgBudgetUtilization",
];

const PLAN_KEYS: BudgetSpreadsheetColumnKey[] = [
    "planName",
    "planType",
    "planDescription",
    "planStatus",
    "planAllocatedAmount",
    "planStartDate",
    "planEndDate",
];

const LINE_ITEM_KEYS: BudgetSpreadsheetColumnKey[] = [
    "lineItemName",
    "lineCategoryName",
    "lineItemDescription",
    "lineItemComments",
    "linePlannedAmount",
    "lineItemActualAmount",
    "lineItemCommittedAmount",
];

export class BudgetSpreadsheetUtil {
    public static flattenExport(
        exportPayload: Budget.OrgBudgetExport,
        options?: OrganizationExportFlattenOptions,
    ): BudgetExportFlatRow[] {
        const budgetOptions = BudgetSpreadsheetUtil.resolveBudgetOptions(options?.budget);
        const organizationName = exportPayload.organizationName ?? "";
        const organizationId = exportPayload.organizationId;
        const rows: BudgetExportFlatRow[] = [];

        for (const orgBudget of exportPayload.orgBudgets ?? []) {
            const orgRowBase = BudgetSpreadsheetUtil.orgRowBaseFromBudget(organizationId, organizationName, orgBudget);

            if (budgetOptions.includeLineItems) {
                BudgetSpreadsheetUtil.forEachPlanAndLineItem(orgBudget, (budgetPlan, lineItem) => {
                    rows.push(BudgetSpreadsheetUtil.buildFlatRow(orgRowBase, budgetPlan, lineItem, budgetOptions));
                });
                continue;
            }

            if (budgetOptions.includeBudgetPlans) {
                BudgetSpreadsheetUtil.forEachPlan(orgBudget, (budgetPlan) => {
                    rows.push(BudgetSpreadsheetUtil.buildFlatRow(orgRowBase, budgetPlan, undefined, budgetOptions));
                });
                continue;
            }

            if (budgetOptions.includeOrgBudgetSummary) {
                rows.push(BudgetSpreadsheetUtil.buildFlatRow(orgRowBase, undefined, undefined, budgetOptions));
            }
        }

        return rows;
    }

    public static columnsForOptions(options?: OrganizationExportFlattenOptions): BudgetSpreadsheetColumnDef[] {
        const keys = new Set(BudgetSpreadsheetUtil.headerKeysForOptions(options));
        return BUDGET_SPREADSHEET_COLUMNS.filter((column) => keys.has(column.key));
    }

    public static csvColumnsForOptions(options?: OrganizationExportFlattenOptions): CsvColumn<BudgetExportFlatRow>[] {
        return BudgetSpreadsheetUtil.columnsForOptions(options).map((column) => ({
            key: column.key,
            label: column.header,
        }));
    }

    public static rowsToCsv(rows: BudgetExportFlatRow[], options?: OrganizationExportFlattenOptions): string {
        return SpreadSheetService.rowsToCsv(rows, BudgetSpreadsheetUtil.csvColumnsForOptions(options));
    }

    public static flatRowsToSheetEntries(
        rows: BudgetExportFlatRow[],
        options?: OrganizationExportFlattenOptions,
    ): SheetEntry[] {
        const columns = BudgetSpreadsheetUtil.columnsForOptions(options);
        return rows.map((flatRow) => {
            const entry: Record<string, string | number> = {};
            for (const column of columns) {
                const cellValue = flatRow[column.key];
                entry[column.header] = cellValue === undefined || cellValue === null ? "" : cellValue;
            }
            return entry;
        });
    }

    public static async downloadExport(
        rows: BudgetExportFlatRow[],
        filename: string,
        options?: OrganizationExportFlattenOptions,
    ): Promise<void> {
        // Import UI loads via SheetJS (xlsx); export as .xlsx so the same file can be re-uploaded.
        await SpreadSheetService.downloadExcel(
            BudgetSpreadsheetUtil.flatRowsToSheetEntries(rows, options),
            filename,
            "Budget Export",
        );
    }

    public static flattenBulkExport(
        exports: Budget.OrgBudgetExport[],
        options?: OrganizationExportFlattenOptions,
    ): BudgetExportFlatRow[] {
        const rows: BudgetExportFlatRow[] = [];
        for (const exportEntry of exports ?? []) {
            rows.push(...BudgetSpreadsheetUtil.flattenExport(exportEntry, options));
        }
        return rows;
    }

    public static buildExportFileName(organizationName: string): string {
        return BudgetSpreadsheetUtil.buildBulkExportFileName(organizationName || "organization");
    }

    public static buildBulkExportFileName(label: string): string {
        const safeName = (label || "export")
            .replace(/[^\w\s-]/g, "")
            .trim()
            .replace(/\s+/g, "-")
            .toLowerCase();
        const dateStamp = new Date().toISOString().slice(0, 10);
        return `budget-export-${safeName}-${dateStamp}.xlsx`;
    }

    private static resolveBudgetOptions(
        options?: Partial<OrganizationExport.BudgetOptions>,
    ): OrganizationExport.BudgetOptions {
        return { ...OrganizationExport.DEFAULT_BUDGET_OPTIONS, ...options };
    }

    private static headerKeysForOptions(options?: OrganizationExportFlattenOptions): BudgetSpreadsheetColumnKey[] {
        const budgetOptions = BudgetSpreadsheetUtil.resolveBudgetOptions(options?.budget);
        const { includeOrgColumns, includePlanColumns } = BudgetSpreadsheetUtil.columnFlags(budgetOptions);
        const headers: BudgetSpreadsheetColumnKey[] = [...IDENTITY_KEYS];
        if (includeOrgColumns) {
            headers.push(...ORG_BUDGET_KEYS);
        }
        if (includePlanColumns) {
            headers.push(...PLAN_KEYS);
        }
        if (budgetOptions.includeLineItems) {
            headers.push(...LINE_ITEM_KEYS);
        }
        return headers.length > IDENTITY_KEYS.length ? headers : BUDGET_SPREADSHEET_COLUMNS.map((column) => column.key);
    }

    private static orgRowBaseFromBudget(
        organizationId: number | undefined,
        organizationName: string,
        orgBudget: Budget.OrgBudget,
    ): Pick<
        BudgetExportFlatRow,
        | "organizationId"
        | "organizationName"
        | "fiscalYearName"
        | "budgetName"
        | "orgBudgetDescription"
        | "totalAllocated"
        | "orgBudgetTotalSpent"
        | "orgBudgetTotalCommitted"
        | "orgBudgetUtilization"
    > {
        const fiscalYearName = orgBudget.fiscalYear?.name ?? orgBudget.fiscalYearName ?? "";
        return {
            organizationId: organizationId ?? orgBudget.organizationId ?? "",
            organizationName,
            fiscalYearName,
            budgetName: orgBudget.name ?? "",
            orgBudgetDescription: orgBudget.description ?? "",
            totalAllocated: orgBudget.totalAllocated ?? 0,
            orgBudgetTotalSpent: orgBudget.totalSpent ?? 0,
            orgBudgetTotalCommitted: orgBudget.totalCommitted ?? 0,
            orgBudgetUtilization: Number(Budget.getUtilization(orgBudget).toFixed(1)),
        };
    }

    private static forEachPlan(
        orgBudget: Budget.OrgBudget,
        perPlan: (budgetPlan: Budget.BudgetPlan | undefined) => void,
    ): void {
        const plans = orgBudget.budgetPlans?.length ? orgBudget.budgetPlans : [undefined];
        for (const budgetPlan of plans) {
            perPlan(budgetPlan);
        }
    }

    private static forEachPlanAndLineItem(
        orgBudget: Budget.OrgBudget,
        perLineItem: (budgetPlan: Budget.BudgetPlan | undefined, lineItem: Budget.BudgetLineItem | undefined) => void,
    ): void {
        BudgetSpreadsheetUtil.forEachPlan(orgBudget, (budgetPlan) => {
            const lineItems = budgetPlan?.budgetLineItems?.length ? budgetPlan.budgetLineItems : [undefined];
            for (const lineItem of lineItems) {
                perLineItem(budgetPlan, lineItem);
            }
        });
    }

    private static columnFlags(budgetOptions: OrganizationExport.BudgetOptions): {
        includeOrgColumns: boolean;
        includePlanColumns: boolean;
    } {
        return {
            includeOrgColumns:
                budgetOptions.includeOrgBudgetSummary ||
                budgetOptions.includeBudgetPlans ||
                budgetOptions.includeLineItems,
            includePlanColumns: budgetOptions.includeBudgetPlans || budgetOptions.includeLineItems,
        };
    }

    private static buildFlatRow(
        orgRowBase: Pick<
            BudgetExportFlatRow,
            | "organizationId"
            | "organizationName"
            | "fiscalYearName"
            | "budgetName"
            | "orgBudgetDescription"
            | "totalAllocated"
            | "orgBudgetTotalSpent"
            | "orgBudgetTotalCommitted"
            | "orgBudgetUtilization"
        >,
        budgetPlan: Budget.BudgetPlan | undefined,
        lineItem: Budget.BudgetLineItem | undefined,
        budgetOptions: OrganizationExport.BudgetOptions,
    ): BudgetExportFlatRow {
        const { includeOrgColumns, includePlanColumns } = BudgetSpreadsheetUtil.columnFlags(budgetOptions);
        return {
            organizationId: orgRowBase.organizationId,
            organizationName: orgRowBase.organizationName,
            fiscalYearName: orgRowBase.fiscalYearName,
            budgetName: includeOrgColumns ? orgRowBase.budgetName : "",
            orgBudgetDescription: includeOrgColumns ? orgRowBase.orgBudgetDescription : "",
            totalAllocated: includeOrgColumns ? orgRowBase.totalAllocated : 0,
            orgBudgetTotalSpent: includeOrgColumns ? orgRowBase.orgBudgetTotalSpent : 0,
            orgBudgetTotalCommitted: includeOrgColumns ? orgRowBase.orgBudgetTotalCommitted : 0,
            orgBudgetUtilization: includeOrgColumns ? orgRowBase.orgBudgetUtilization : 0,
            planName: includePlanColumns ? (budgetPlan?.name ?? "") : "",
            planType: includePlanColumns ? (budgetPlan?.planType ?? "") : "",
            planDescription: includePlanColumns ? (budgetPlan?.description ?? "") : "",
            planStatus: includePlanColumns ? (budgetPlan?.workflowStatus ?? (budgetPlan ? "Draft" : "")) : "",
            planAllocatedAmount: BudgetSpreadsheetUtil.planAllocatedAmountForExport(budgetPlan, includePlanColumns),
            planStartDate: includePlanColumns ? BudgetSpreadsheetUtil.formatSpreadsheetDate(budgetPlan?.startDate) : "",
            planEndDate: includePlanColumns ? BudgetSpreadsheetUtil.formatSpreadsheetDate(budgetPlan?.endDate) : "",
            lineItemName: budgetOptions.includeLineItems ? (lineItem?.name ?? "") : "",
            lineCategoryName: budgetOptions.includeLineItems ? (lineItem?.categoryName ?? "") : "",
            lineItemDescription: budgetOptions.includeLineItems ? (lineItem?.description ?? "") : "",
            lineItemComments: budgetOptions.includeLineItems ? (lineItem?.comments ?? "") : "",
            linePlannedAmount: BudgetSpreadsheetUtil.lineAmountForExport(
                lineItem,
                budgetOptions.includeLineItems,
                (item) => item.plannedAmount,
            ),
            lineItemActualAmount: BudgetSpreadsheetUtil.lineAmountForExport(
                lineItem,
                budgetOptions.includeLineItems,
                (item) => item.actualAmount,
            ),
            lineItemCommittedAmount: BudgetSpreadsheetUtil.lineAmountForExport(
                lineItem,
                budgetOptions.includeLineItems,
                (item) => item.committedAmount,
            ),
        };
    }

    private static formatSpreadsheetDate(value: string | undefined | null): string {
        if (!value?.trim()) {
            return "";
        }
        const parsed = S25Util.date.parse(value);
        if (!parsed || !S25Util.date.isValid(parsed)) {
            return value.trim();
        }
        return S25Util.date.toS25ISODateStr(parsed) ?? value.trim();
    }

    private static planAllocatedAmountForExport(
        budgetPlan: Budget.BudgetPlan | undefined,
        includePlanColumns: boolean,
    ): string | number {
        if (!includePlanColumns || !budgetPlan) {
            return "";
        }
        return BudgetSpreadsheetUtil.numericAmount(budgetPlan.allocatedAmount);
    }

    private static lineAmountForExport(
        lineItem: Budget.BudgetLineItem | undefined,
        includeLineItems: boolean,
        pickAmount: (item: Budget.BudgetLineItem) => number | undefined,
    ): string | number {
        if (!includeLineItems || !lineItem) {
            return "";
        }
        return pickAmount(lineItem) ?? 0;
    }

    private static numericAmount(value: number | { source: string; parsedValue: number } | undefined): number {
        if (value === undefined || value === null) {
            return 0;
        }
        if (typeof value === "number") {
            return value;
        }
        return value.parsedValue ?? 0;
    }
}
